import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addWallpaper, getSizes } from '../../services/api';
import RetailerSidebar from './RetailerSidebar';

export default function AddWallpaper() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    product_size_id: '',
    application_type: 'wall',
    surface_type: '',
    price: '',
    sku: '',
    pieces_per_box: '',
    price_unit: 'Box',
    coverage_area_per_box: '',
    coverage_area_unit: 'Per Sqmr',
    weight_per_box: '',
    weight_unit: 'Kg',
    thickness_mm: '',
    images: []
  });

  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSizes();
  }, []);

  const fetchSizes = async () => {
    const token = localStorage.getItem('retailer_token');
    
    try {
      const response = await getSizes();
      if (response.success) {
        setSizes(response.sizes);
      }
    } catch (err) {
      console.error('Failed to fetch sizes:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'images' ? files : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('retailer_token');
    

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'images') {
          for (let i = 0; i < formData.images.length; i++) {
            formDataToSend.append('images', formData.images[i]);
          }
        } else if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await addWallpaper(formDataToSend);
      if (response.success) {
        alert('Wallpaper added successfully!');
        navigate('/retailer/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <RetailerSidebar />
      
      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Add New Wallpaper</h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Product Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Size *</label>
                <select
                  name="product_size_id"
                  value={formData.product_size_id}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                >
                  <option value="">Select a size</option>
                  {sizes.map((size) => (
                    <option key={size.id} value={size.id}>
                      {size.name} ({size.width}x{size.height} {size.unit})
                    </option>
                  ))}
                </select>
                {sizes.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    No sizes available. <button type="button" onClick={() => navigate('/retailer/add-size')} className="text-accent hover:underline">Add a size first</button>
                  </p>
                )}
              </div>

              {/* Application Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Application Type *</label>
                <select
                  name="application_type"
                  value={formData.application_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                >
                  <option value="wall">Wall</option>
                  <option value="floor">Floor</option>
                  <option value="both">Both</option>
                </select>
              </div>

              {/* Surface Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Surface Type *</label>
                <input
                  type="text"
                  name="surface_type"
                  value={formData.surface_type}
                  onChange={handleChange}
                  required
                  placeholder="e.g., vinyl, fabric, textured"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                <input
                  type="number"
                  step="0.01"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  placeholder="Enter price"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                />
              </div>

              {/* Price Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price Unit *</label>
                <select
                  name="price_unit"
                  value={formData.price_unit}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                >
                  <option value="Box">Box</option>
                  <option value="Roll">Roll</option>
                  <option value="Sqft">Sqft</option>
                  <option value="Sqmr">Sqmr</option>
                </select>
              </div>

              {/* SKU */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SKU (Optional)</label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  placeholder="Product code (auto-generated if empty)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                />
              </div>

              {/* Pieces per Box */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pieces/Rolls per Box (Optional)</label>
                <input
                  type="number"
                  name="pieces_per_box"
                  value={formData.pieces_per_box}
                  onChange={handleChange}
                  placeholder="Number of pieces or rolls per box"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                />
              </div>

              

              {/* Coverage Area per Box */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Coverage Area</label>
                <input
                  type="number"
                  step="0.01"
                  name="coverage_area_per_box"
                  value={formData.coverage_area_per_box}
                  onChange={handleChange}
                  placeholder="Coverage area"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                />
              </div>

              {/* Coverage Area Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Coverage Area Unit *</label>
                <select
                  name="coverage_area_unit"
                  value={formData.coverage_area_unit}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                >
                  <option value="Per Sqmr">Per Sqmr</option>
                  <option value="Per Sqft">Per Sqft</option>
                </select>
              </div>

              {/* Weight per Box */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Weight</label>
                <input
                  type="number"
                  step="0.01"
                  name="weight_per_box"
                  value={formData.weight_per_box}
                  onChange={handleChange}
                  placeholder="Weight in kg"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                />
              </div>

              {/* Weight Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Weight Unit *</label>
                <select
                  name="weight_unit"
                  value={formData.weight_unit}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                >
                  <option value="Kg">Kg</option>
                  <option value="Lb">Lb</option>
                </select>
              </div>

              {/* Thickness */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Thickness (mm)</label>
                <input
                  type="number"
                  step="0.1"
                  name="thickness_mm"
                  value={formData.thickness_mm}
                  onChange={handleChange}
                  placeholder="Thickness in mm"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Wallpaper Images *</label>
                <input
                  type="file"
                  name="images"
                  onChange={handleChange}
                  multiple
                  accept="image/*"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Upload up to 15 design images</p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-accent hover:bg-accent2 text-white font-semibold py-3 px-6 rounded-lg transition-all disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {loading ? 'Adding Wallpaper...' : 'Add Wallpaper'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/retailer/dashboard')}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
